import { useMutation } from '@apollo/client';
import type { BusinessAddressRef } from '@seek/hirer-business-address';
import { BrandedProductId, PremiumProductId } from '@seek/hirer-products-shim';
import { HirerPromoCodesBanner } from '@seek/hirer-promo-codes-banner';
import { HirerPromoCodesInput } from '@seek/hirer-promo-codes-input';
import { useTranslations } from '@vocab/react';
import { Box, Heading, Stack, ContentBlock } from 'braid-design-system';
import { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { scroller } from 'react-scroll';

import { BackButtonPayAndPost } from 'src/app/shared/components/BackButtonPayAndPost/index.tsx';
import { CheckoutPageError } from 'src/app/shared/components/CheckoutPageError/index.tsx';
import { SystemBanner } from 'src/app/shared/components/SystemBanner/index.tsx';
import { TEALIUM_PAYMENT_METHOD } from 'src/app/shared/constants/dispatcherId.ts';
import {
  CONNECTION_BUSINESS_ADDRESS_ERROR,
  CONNECTION_ERROR,
  ON_CREDIT_HOLD_ERROR,
  CONNECTION_SESSION_EXPIRED_ERROR,
  FORM_VALIDATION_ERROR,
} from 'src/app/shared/constants/errorMessages.ts';
import {
  SECTION_BUSINESS_ADDRESS,
  SECTION_PAY_AND_POST,
} from 'src/app/shared/constants/pageSectionName.ts';
import {
  PAGE_ID_MANAGE,
  PAGE_ID_PAY_AND_POST,
} from 'src/app/shared/constants/pageUid.ts';
import { PaymentOption } from 'src/app/shared/constants/paymentOptions.ts';
import { PRODUCT_SET_SELECTED_PRODUCT_UNAVAILABLE } from 'src/app/shared/constants/productSetStatus.ts';
import { ROUTE_SELECT_AD_TYPE } from 'src/app/shared/constants/routes.ts';
import {
  APPLY_PROMO_CODE_PRESSED,
  NAVIGATION_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants.ts';
import {
  FEATURE_PER_UNIT_BILLING,
  FEATURE_PERMISSIONS,
  useFeatureToggleData,
} from 'src/app/shared/featureToggle/index.ts';
import { getIsBusinessAddressVerified } from 'src/app/utils/getIsBusinessAddressVerified';
import handlePageValidation from 'src/app/utils/handleValidation';
import { log } from 'src/app/utils/logging';
import {
  getPaymentDetailsHidden,
  hasSufficientBudget,
} from 'src/app/utils/payment/getPaymentDetailsHidden';
import { isInvoiceOnly } from 'src/app/utils/payment/isInvoiceOnly';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import dispatchTealium from 'src/app/v1/state/dispatcher/tealium/index.ts';
import { actionUpdatePaymentOption } from 'src/app/v1/state/modules/formData/paymentOption/index.ts';
import {
  actionUpdateCart,
  actionUpdateCartOrderId,
  actionUpdateCartPromoCode,
} from 'src/app/v1/state/modules/session/cart/index.ts';
import { actionUpdateDiscountCode } from 'src/app/v1/state/modules/tealium/discountCode/index.ts';
import { actionUpdatehasSufficientBudget } from 'src/app/v1/state/modules/tealium/hasSufficientBudget/index.ts';
import { actionUpdateIsPayment } from 'src/app/v1/state/modules/tealium/isPayment/index.ts';
import { actionUpdateIsPremiumAdOffered } from 'src/app/v1/state/modules/tealium/isPremiumAdOffered/index.ts';
import { actionUpdateOrderNumber } from 'src/app/v1/state/modules/tealium/orderNumber/index.ts';
import { actionUpdatePaymentMethod } from 'src/app/v1/state/modules/tealium/paymentMethod/index.ts';
import { AdBudgetUpsell } from 'src/app/v1/views/components/AdBudgetUpsell/index.tsx';
import ConnectionError from 'src/app/v1/views/components/ConnectionError/index.tsx';
import { CreditHold } from 'src/app/v1/views/components/CreditHold/index.tsx';
import { FakeErrorComponent } from 'src/app/v1/views/components/FakeErrorComponent/index.tsx';
import { OrderSummary } from 'src/app/v1/views/components/OrderSummary/index.tsx';
import { PremiumUpsell } from 'src/app/v1/views/components/PremiumUpsell/index.tsx';
import { withDesktopEnhancers } from 'src/app/v1/views/enhancers/withDesktopEnhancers/index.tsx';
import {
  InvalidPromoCodeError,
  useCreateOrderWithProductSet,
} from 'src/app/v1/views/hooks/useCreateOrderWithProductSet/index.tsx';
import { useJobCreate } from 'src/app/v1/views/hooks/useJobCreate/index.tsx';
import { useLocalisedRoutes } from 'src/app/v1/views/hooks/useLocalisedRoutes/index.ts';
import PageLayout from 'src/app/v1/views/layouts/PageLayout/index.tsx';
import { BusinessAddressSection } from 'src/app/v1/views/sections/BusinessAddressSection/index.tsx';
import { FaqContactSection } from 'src/app/v1/views/sections/FaqContactSection/index.tsx';
import PageErrorMessage from 'src/app/v1/views/shared/PageErrorMessage/index.tsx';
import {
  BillingStatus,
  useGetOrderSummaryTotalQuery,
} from 'src/graphql/generated';
import { UPDATE_BUSINESS_ADDRESS } from 'src/graphql/updateBusinessAddress';
import type { AppState } from 'src/types/appState';
import type { DispatchDraftResponse } from 'src/types/draft';
import type { CreateFlowPageContainer } from 'src/types/pages';

import { getShowPromoCodesInput } from '../../../../utils/promoCode/getShowPromoCodesInput.ts';
import RoleReqsErrorMessage from '../../components/RoleReqsErrorMessage/index.tsx';
import { UserPermissionsAlert } from '../../components/UserPermissionsAlert/index.tsx';
import { useCallbacksForCreateProductSetForAdPosting } from '../../hooks/useCallbacksForCreateProductSetForAdPosting/index.ts';
import { useCreateProductSet } from '../../hooks/useCreateProductSet/index.ts';

import translations from './.vocab/index.ts';
import { PromoCodeSuccessAlert } from './components/PromoCodeSuccessAlert.tsx';
import { PaymentsSection } from './sections/PaymentsSection.tsx';

interface Props {
  handleDispatchDraft: (
    isCompleted?: boolean,
    rejectOnError?: boolean,
  ) => DispatchDraftResponse;
  handleNextPageRoute: () => void;
  handleProgrammaticRouteChange: (stepId: string) => void;
}

export const PayAndPostPage = ({
  handleDispatchDraft,
  handleNextPageRoute,
  handleProgrammaticRouteChange,
}: Props) => {
  const { t } = useTranslations(translations);

  const businessAddressRef = useRef<BusinessAddressRef>(null);
  const containerComponentMapping: CreateFlowPageContainer[] = [
    {
      containerId: SECTION_BUSINESS_ADDRESS,
      components: [businessAddressRef],
    },
  ];
  const {
    adBudgetId,
    advertiserNation,
    billingStatus,
    isBusinessAddressVerified,
    buyUpSell,
    cart,
    contractBalance,
    countryCode,
    hidePrice,
    isJobPosted,
    isPaymentInProgress,
    jobCreationInProgressCount,
    jobId,
    paymentOption,
    pricingGroupContractExists,
    productSetId,
    productIds,
    productSetStatus,
    productTotalPrice,
    rrFailedToSave,
    seekAdvertiserId,
    seekListingId,
    selectedProductId,
    userPermissions,
  } = useSelector(
    (state: AppState) => ({
      adBudgetId: state.session.adBudget?.id,
      advertiserNation: state.session.hirerAccount.nation,
      billingStatus: state.session.hirerAccount?.billingStatus,
      isBusinessAddressVerified: getIsBusinessAddressVerified(
        state.session.postalAddress,
      ),
      buyUpSell: state.session.buyUpSell,
      cart: state.session.cart,
      contractBalance: state.session.contractBalance,
      countryCode: state.session.hirerAccount.nation,
      hidePrice: state.session.hirerAccount?.hidePrice,
      isJobPosted: state.session.isJobPosted,
      isPaymentInProgress: state.session.isPaymentInProgress,
      jobCreationInProgressCount: state.app.jobCreationInProgressCount,
      jobId: state.session.job?.id,
      paymentOption: state.formData.paymentOption,
      pricingGroupContractExists:
        state.session.hirerAccount.pricingGroupContractExists,
      productIds: state.session.productSet?.items
        .map((item) => item?.id)
        .flatMap((id) => id || []),
      productSetId: state.session.productSet?.id,
      productSetStatus: state.session.productSetStatus,
      productTotalPrice: state.tealium.productTotalPrice,
      rrFailedToSave: state.session.rrFailedToSave,
      seekAdvertiserId: state.session.seekAdvertiserId,
      seekListingId: state.tealium.seekListingId,
      selectedProductId: state.formData.selectedProductId,
      userPermissions: state.session.userPermissions,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();
  const { goToRoute } = useLocalisedRoutes();

  const [displayPageError, setDisplayPageError] = useState<boolean>(false);
  const [hidePaymentDetails, setHidePaymentDetails] = useState<boolean>(false);

  const [orderError, setOrderError] = useState<Error | null>(null);
  const [orderExpired, setOrderExpired] = useState<boolean>(false);
  const [promoCodeSuccess, setPromoCodeSuccess] = useState<
    string | undefined
  >();
  const isPerUnitBilling = useFeatureToggleData(FEATURE_PER_UNIT_BILLING);
  const isPermissionsEnabled = useFeatureToggleData(FEATURE_PERMISSIONS);

  const { data: orderSummaryData } = useGetOrderSummaryTotalQuery({
    variables: {
      orderId: cart.orderId!,
      advertiserId: seekAdvertiserId,
      advertiserNation,
    },
    skip: !cart.orderId,
    onError: (error) => {
      log.error('PayAndPost | GetOrderSummaryTotalQuery', {
        error,
      });
    },
  });

  const [
    updateBusinessAddress,
    {
      error: updateBusinessAddressError,
      loading: updateBusinessAddressLoading,
    },
  ] = useMutation(UPDATE_BUSINESS_ADDRESS, {
    onError(error) {
      log.error('PayAndPost | UpdateBusinessAddress', {
        error,
      });
      throw error;
    },
  });

  const totalOrderNetPrice = orderSummaryData?.orderSummary?.lineItems.reduce(
    (acc, current) => acc + current.price.netPrice.value,
    0,
  );

  const isPricingGroupWithZeroPrice =
    Boolean(pricingGroupContractExists) && Number(productTotalPrice) === 0;

  const isPaymentDetailsHidden = getPaymentDetailsHidden({
    contractBalance,
    hidePaymentDetails,
    promoCodeSuccess,
    totalOrderNetPrice,
    isPerUnitBilling,
  });

  const createPendingJob = useJobCreate();

  const createProductSetCallbacks =
    useCallbacksForCreateProductSetForAdPosting();
  const createProductSetCallback = useCreateProductSet(
    createProductSetCallbacks,
  );
  const createOrderWithProductSet = useCreateOrderWithProductSet(
    createProductSetCallback,
  );

  const isPremiumUpsellVisible =
    ((selectedProductId && selectedProductId === BrandedProductId) ||
      (buyUpSell && selectedProductId === PremiumProductId)) &&
    !isPricingGroupWithZeroPrice;

  const onPromoCodeSuccess = useCallback(
    (promoCode: string) => {
      dispatch(actionUpdateDiscountCode(promoCode));

      const eventData = {
        eventType: 'apply_promo_code_succeeded',
        eventName: APPLY_PROMO_CODE_PRESSED,
        discountCode: promoCode,
      };

      getTrackingAdapter.sendLinkEvent(eventData);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  ); // disabling because tealium object is unstable

  const onPromoCodeInvalid = useCallback(
    (promoCode: string, errorMessage?: string) => {
      const eventData = {
        eventType: 'apply_promo_code_failed',
        eventName: FORM_VALIDATION_ERROR,
        discountCode: promoCode,
        validationType: 'field',
        errorMessage: errorMessage ?? 'Enter a valid promo code',
      };

      getTrackingAdapter.sendLinkEvent(eventData);

      dispatch(actionUpdateCartPromoCode(''));
    },
    [dispatch],
  );

  const onPromoCodeFailure = useCallback(
    (promoCode: string | undefined) => {
      dispatch(actionUpdateCartPromoCode(''));

      const eventData = {
        eventType: 'apply_promo_code_failed',
        eventName: FORM_VALIDATION_ERROR,
        discountCode: promoCode,
        validationType: 'field',
        errorMessage: 'Enter a valid promo code',
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  ); // disabling because tealium object is unstable

  useEffect(() => {
    if (
      productSetId &&
      selectedProductId &&
      !isJobPosted &&
      !isPaymentInProgress
    ) {
      const saveDraftAndCreateJob = async () => {
        await handleDispatchDraft(false);
        return createPendingJob();
      };
      saveDraftAndCreateJob()
        .then((data) => {
          if (data?.jobId && data?.revisionId) {
            dispatch(
              actionUpdateCart([
                {
                  type: 'AdProduct',
                  productSetId,
                  productId: selectedProductId,
                  jobId: data.jobId,
                  revisionId: data.revisionId,
                },
              ]),
            );
          }
        })
        .catch((error) => {
          setOrderError(error);
        });
    }
    // Execute this code when the page loads or the billing country changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    advertiserNation,
    productSetId,
    isJobPosted,
    isPaymentInProgress,
    selectedProductId,
  ]);

  useEffect(() => {
    if (
      !isJobPosted &&
      !isPaymentInProgress &&
      cart.lineItems.length > 0 &&
      cart.lineItems.every((item) => item.productSetId === productSetId) &&
      productSetId
    ) {
      createOrderWithProductSet(
        cart.lineItems,
        productSetId,
        cart.promoCode,
      ).then((data) => {
        dispatch(actionUpdateCartOrderId(data.orderId ?? null));
        dispatch(actionUpdateOrderNumber(data.orderNumber ?? null));
        setPromoCodeSuccess(data.promoCode);
        if (data.error) {
          log.error('PayAndPost | createOrder', data.error, true);

          // if order was created with promo code, display error message and recreate order without promo code
          if (data.error instanceof InvalidPromoCodeError) {
            return onPromoCodeFailure(cart.promoCode);
          }
        }
        setOrderError(data.error ?? null);
        // create order with promo code was successful, clear any error message
        if (data.promoCode) {
          return onPromoCodeSuccess(data.promoCode);
        }

        if (cart.promoCode) {
          log.error('PayAndPost | createOrder | missing promo code', data);
        }
      });
    }
  }, [
    cart.lineItems,
    cart.promoCode,
    createOrderWithProductSet,
    dispatch,
    onPromoCodeFailure,
    onPromoCodeSuccess,
    productSetId,
    isJobPosted,
    isPaymentInProgress,
  ]);

  useEffect(() => {
    if (totalOrderNetPrice === 0 && paymentOption !== PaymentOption.INVOICE) {
      dispatch(actionUpdatePaymentOption(PaymentOption.INVOICE));
      dispatch(actionUpdatePaymentMethod(PaymentOption.INVOICE));
    }
    if (!totalOrderNetPrice) {
      dispatch(actionUpdateIsPayment(false));
    } else {
      dispatch(actionUpdateIsPayment(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalOrderNetPrice]);

  // effect to select payment option when upsell budget is selected
  // only when there isn't a payment in progress and the job hasn't been posted
  // this is to prevent payment option from resetting when coming back from redirection
  useEffect(() => {
    if (
      cart.orderId &&
      !isPaymentDetailsHidden &&
      !isJobPosted &&
      !isPaymentInProgress
    ) {
      if (isPerUnitBilling) {
        dispatch(actionUpdatePaymentOption(PaymentOption.INVOICE));
        dispatch(actionUpdatePaymentMethod(PaymentOption.INVOICE));
      } else {
        dispatch(actionUpdatePaymentOption(PaymentOption.CREDIT_CARD));
        dispatch(actionUpdatePaymentMethod(PaymentOption.CREDIT_CARD));
      }
    }
  }, [
    cart.orderId,
    dispatch,
    isPaymentDetailsHidden,
    isJobPosted,
    isPaymentInProgress,
    isPerUnitBilling,
  ]);

  useEffect(() => {
    if (isPremiumUpsellVisible) {
      dispatch(actionUpdateIsPremiumAdOffered(true));
    } else {
      dispatch(actionUpdateIsPremiumAdOffered(false));
    }
  }, [dispatch, isPremiumUpsellVisible]);
  // Only to set up payment option to invoice in case of 0 value
  useEffect(() => {
    if (cart.orderId && isPricingGroupWithZeroPrice) {
      dispatch(actionUpdatePaymentOption(PaymentOption.INVOICE));
      dispatch(actionUpdatePaymentMethod(PaymentOption.INVOICE));
    }
  }, [cart.orderId, dispatch, isPricingGroupWithZeroPrice]);

  useEffect(() => {
    if (!updateBusinessAddressLoading && updateBusinessAddressError) {
      scroller.scrollTo(SECTION_PAY_AND_POST, {
        duration: 500,
        delay: 100,
        smooth: true,
      });
    }
  }, [updateBusinessAddressError, updateBusinessAddressLoading]);

  useEffect(() => {
    // only redirect to success page once the following props are available
    if (seekListingId !== null && isJobPosted) {
      handleNextPageRoute();
      return;
    }
  }, [handleNextPageRoute, isJobPosted, seekListingId]);

  useEffect(() => {
    if (
      paymentOption === PaymentOption.INVOICE ||
      isInvoiceOnly(contractBalance)
    ) {
      dispatch(
        dispatchTealium(
          TEALIUM_PAYMENT_METHOD,
          Number(contractBalance) > 0
            ? PaymentOption.CONTRACT
            : PaymentOption.INVOICE,
        ),
      );
    }
  }, [paymentOption, contractBalance, dispatch]);

  useEffect(() => {
    dispatch(
      actionUpdatehasSufficientBudget(
        hasSufficientBudget(contractBalance, totalOrderNetPrice),
      ),
    );
  }, [contractBalance, dispatch, totalOrderNetPrice]);

  const getAdditionalErrorFields = () => ({
    adBudgetId,
    advertiserNation,
    billingStatus,
    isBusinessAddressVerified,
    buyUpSell,
    cartOrderId: cart.orderId,
    countryCode,
    hidePrice,
    isJobPosted,
    jobCreationInProgressCount,
    jobId,
    productTotalPrice,
    rrFailedToSave,
    seekAdvertiserId,
    seekListingId,
    productSetId,
    productSetStatus,
  });

  const handleValidation = (skipIfNull = false) =>
    handlePageValidation(containerComponentMapping, skipIfNull);

  const renderPageErrorMessage = () => {
    if (!displayPageError) {
      return;
    }

    const errorMessage = getPageErrorMessage();

    switch (errorMessage) {
      case CONNECTION_BUSINESS_ADDRESS_ERROR:
        return (
          <ConnectionError errorType={CONNECTION_BUSINESS_ADDRESS_ERROR} />
        );
      case CONNECTION_SESSION_EXPIRED_ERROR:
        return <ConnectionError errorType={CONNECTION_SESSION_EXPIRED_ERROR} />;
      case ON_CREDIT_HOLD_ERROR:
        return <CreditHold renderAlert={false} />;
      case CONNECTION_ERROR:
        return <ConnectionError errorType={CONNECTION_ERROR} />;
      default:
        return;
    }
  };

  const getPageErrorMessage = () => {
    if (updateBusinessAddressError) {
      return CONNECTION_BUSINESS_ADDRESS_ERROR;
    } else if (orderExpired) {
      return CONNECTION_SESSION_EXPIRED_ERROR;
    } else if (billingStatus === BillingStatus.ON_CREDIT_HOLD) {
      return ON_CREDIT_HOLD_ERROR;
    }

    return CONNECTION_ERROR;
  };

  const handleHidePaymentDetails = () => {
    setHidePaymentDetails(true);
  };

  if (productSetStatus === PRODUCT_SET_SELECTED_PRODUCT_UNAVAILABLE) {
    goToRoute(ROUTE_SELECT_AD_TYPE);
    return null;
  }

  if (!selectedProductId) {
    log.error('Missing ad product value', { ...getAdditionalErrorFields() });
    goToRoute(ROUTE_SELECT_AD_TYPE);
    return null;
  }

  const onPromoCodeApply = (promoCode: string) => {
    if (promoCode === cart.promoCode) {
      return;
    }
    setPromoCodeSuccess(undefined);

    const promoCodeEventData = {
      eventType: 'apply_promo_code_pressed',
      eventName: APPLY_PROMO_CODE_PRESSED,
      discountCode: promoCode,
    };

    getTrackingAdapter.sendLinkEvent(promoCodeEventData);
  };

  const onPromoCodeValidated = (promoCode: string) => {
    dispatch(actionUpdateCartPromoCode(promoCode));
  };

  const onPromoCodeClear = () => {
    setPromoCodeSuccess(undefined);
    dispatch(actionUpdateCartPromoCode(''));
  };

  const handleOnClickEvent = () => {
    if (isPaymentInProgress) {
      return;
    }

    handleProgrammaticRouteChange(PAGE_ID_MANAGE);

    getTrackingAdapter.sendLinkEvent({
      eventName: NAVIGATION_PRESSED,
      objectInteraction: 'ecaja-checkout-back-interacted',
    });
  };

  // Determine if there are any errors that should prevent page from rendering
  const isCreditOnHold = billingStatus === BillingStatus.ON_CREDIT_HOLD;
  const isAllowedToCreateJobs = isPermissionsEnabled
    ? userPermissions.canCreateJobs === true
    : true;
  const getPageBlockError = () => {
    if (!isAllowedToCreateJobs) {
      return <UserPermissionsAlert />;
    }
    if (isCreditOnHold) {
      return <CreditHold renderAlert={true} />;
    }
    return null;
  };
  const pageBlockError = getPageBlockError();

  const showPromoCodeInput = getShowPromoCodesInput({
    promoCodeSuccess,
    totalOrderNetPrice,
  });

  return (
    <PageLayout
      belowContentArea={<FaqContactSection />}
      billingStatus={billingStatus}
      hasAside
      hasProgressBar={false}
      handleProgrammaticRouteChange={handleProgrammaticRouteChange}
      pageErrorMessage={renderPageErrorMessage()}
      rrFailedToSave={rrFailedToSave}
      handleValidation={handleValidation}
      handleDispatchDraft={handleDispatchDraft}
    >
      <Stack space="medium">
        <Stack space="large">
          <BackButtonPayAndPost onClick={handleOnClickEvent}>
            {t('pay-post-page-link-text')}
          </BackButtonPayAndPost>
          <Heading level="3" component="h2">
            {t('pay-post-page-heading')}
          </Heading>
        </Stack>

        {pageBlockError ? (
          <Box marginBottom="medium">{pageBlockError}</Box>
        ) : (
          <>
            {/*
             * buyUpsell should only be set in create flow via the PremiumUpsell component
             * If PremiumUpsell is not rendered, buyUpsell should not be able to be updated in store
             * If buyUpsell is set in store after upgrading, we need to check selectedProductId is PremiumProductId
             * in case hirer upgrades and then selects a Classic ad
             */}
            {isPremiumUpsellVisible && <PremiumUpsell />}

            {!isBusinessAddressVerified && (
              <BusinessAddressSection businessAddressRef={businessAddressRef} />
            )}

            {!hidePrice && !promoCodeSuccess && Boolean(seekAdvertiserId) && (
              <AdBudgetUpsell />
            )}

            {!Boolean(adBudgetId) && showPromoCodeInput && (
              <ContentBlock width="medium">
                <Stack space="gutter">
                  <HirerPromoCodesInput
                    productId={selectedProductId}
                    bannerToDisplay={
                      <HirerPromoCodesBanner
                        productIds={productIds}
                        padding={{ paddingX: 'medium', paddingY: 'medium' }}
                      />
                    }
                    onClear={onPromoCodeClear}
                    onError={(errorMessage) =>
                      onPromoCodeInvalid(
                        cart.promoCode ?? '',
                        errorMessage.message,
                      )
                    }
                    onInvalid={(errorMessage, promoCode) =>
                      onPromoCodeInvalid(promoCode, errorMessage)
                    }
                    onSuccess={onPromoCodeValidated}
                    onClick={onPromoCodeApply}
                  />
                  {Boolean(promoCodeSuccess) && (
                    <PromoCodeSuccessAlert
                      promoCode={cart.promoCode ?? ''}
                      free={totalOrderNetPrice === 0}
                    />
                  )}
                </Stack>
              </ContentBlock>
            )}

            {orderError && <CheckoutPageError />}

            {!orderError && (
              <OrderSummary
                hidePaymentDetails={isPaymentDetailsHidden}
                handleHidePaymentDetails={handleHidePaymentDetails}
              />
            )}

            <SystemBanner context="job_posting_pay_and_post" style="alert" />
            {!orderError && (
              <PaymentsSection
                businessAddressRef={businessAddressRef}
                handleDispatchDraft={handleDispatchDraft}
                handleValidation={handleValidation}
                setOrderExpired={setOrderExpired}
                setDisplayPageError={setDisplayPageError}
                isPaymentDetailsHidden={isPaymentDetailsHidden}
                updateBusinessAddress={updateBusinessAddress}
              />
            )}
          </>
        )}
      </Stack>
      <FakeErrorComponent>
        <RoleReqsErrorMessage />
        <PageErrorMessage hasError={true}>
          <ConnectionError errorType={CONNECTION_BUSINESS_ADDRESS_ERROR} />
        </PageErrorMessage>
      </FakeErrorComponent>
    </PageLayout>
  );
};

export default withDesktopEnhancers(PAGE_ID_PAY_AND_POST)(PayAndPostPage);
